import axios from "axios";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

const AUTH_DISABLED = false;

const AuthContext = createContext({
  user: null,
  loading: true,
  refresh: async () => {},
  login: () => {},
  logout: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (AUTH_DISABLED) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const resp = await axios.get("/api/me", { withCredentials: true });
      if (resp.data && resp.data.authenticated) {
        setUser(resp.data.user || null);
      } else {
        setUser(null);
      }
    } catch (_err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onFocus = () => { refresh(); };
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    const onStorage = (e) => {
      if (e.key === "adx_auth_event") refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("storage", onStorage);
    };
  }, [refresh]);

  const login = useCallback((nextPath) => {
    const next = nextPath || window.location.pathname + window.location.search;
    window.location.href = `/auth/start?next=${encodeURIComponent(next)}`;
  }, []);

  const logout = useCallback(() => {
    window.location.href = "/logout";
  }, []);

  const value = useMemo(
    () => ({ user, loading, refresh, login, logout }),
    [user, loading, refresh, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

export function RequireAuth({ children }) {
  if (AUTH_DISABLED) {
    return children;
  }
  const { user, loading } = useAuth();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const allowEmbeddedLoginPanel =
    location.pathname === "/dashboard" && params.get("login") === "1";

  useEffect(() => {
    if (!loading && !user && !allowEmbeddedLoginPanel) {
      const next = location.pathname + location.search;
      window.location.replace(`/auth/start?next=${encodeURIComponent(next)}`);
    }
  }, [loading, user, location.pathname, location.search, allowEmbeddedLoginPanel]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white text-slate-500">
        <div className="flex items-center gap-2 text-sm">
          <span className="h-3 w-3 animate-pulse rounded-full bg-[#0028CD]" />
          Checking your session…
        </div>
      </div>
    );
  }
  if (!user && allowEmbeddedLoginPanel) {
    return children;
  }
  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white text-slate-500">
        <div className="flex items-center gap-2 text-sm">
          <span className="h-3 w-3 animate-pulse rounded-full bg-[#0028CD]" />
          Redirecting to sign in…
        </div>
      </div>
    );
  }
  return children;
}
